// ./src/components/generic/Favicon/Favicon.tsx
//
// Puts the brand into the page head — A2e #101: the tab and home-screen icons, the browser tint, the iOS home-screen
// name and the installed app's MANIFEST, all from the white label of this host (utils/appBrand), re-applied whenever it
// loads or changes. index.html carries the generic set for the very first paint and no manifest at all: one build
// serves every brand, so a manifest written at build time could only name one of them.
import { useEffect } from "react";

import { useBranding } from "@/contexts/BrandingContext";
import { AppBrand, buildAppManifest, iconLinksOf, ManifestIcon, manifestIconOf } from "@/utils/appBrand";

/** How long the icon may take to measure before the manifest falls back to the generic set. */
const ICON_MEASURE_MS = 5000;

/** The icon's natural size (an <img> needs no CORS for that), or 0×0 when it does not load in time. */
const measureIcon = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    const img = new Image();
    const timer = window.setTimeout(() => resolve({ width: 0, height: 0 }), ICON_MEASURE_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });

const metaNamed = (name: string): HTMLMetaElement => {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }

  return meta;
};

/** The manifest as a data: URL — identical content gives an identical URL, so the browser does not re-read it. */
const applyManifest = (brand: AppBrand, icon: ManifestIcon | null): void => {
  const manifest = buildAppManifest(brand, window.location.origin, icon);
  const href = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
  let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
};

export const Favicon = () => {
  const { brand } = useBranding();

  useEffect(() => {
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((link) => link.remove());
    iconLinksOf(brand).forEach((linkData) => {
      const link = document.createElement("link");
      Object.entries(linkData).forEach(([key, value]) => {
        link.setAttribute(key, value as string);
      });
      document.head.appendChild(link);
    });

    metaNamed("theme-color").content = brand.themeColor;
    // The home-screen name iOS gives the app ("Add to Home Screen" reads it then).
    metaNamed("apple-mobile-web-app-title").content = brand.name;

    // The installed app: the brand's icon only when it can be one (square, large enough) — measured, so a logo that
    // is not square never becomes a squashed app icon; otherwise the generic set.
    let active = true;
    const iconUrl = brand.iconUrl;
    if (!iconUrl) {
      applyManifest(brand, null);
    } else {
      measureIcon(iconUrl).then(({ width, height }) => {
        if (active) {
          applyManifest(brand, manifestIconOf(iconUrl, width, height));
        }
      });
    }

    return () => {
      active = false;
    };
  }, [brand]);

  return null;
};
